import { useState, useEffect, useRef, useCallback } from "react";
import axios from "../api/axiosConfig";
import { getImageUrl } from "../utils/getImageUrl";
import AvatarCapture from "../components/AvatarCapture";
import DocumentCapture from "../components/DocumentCapture";
import { checkDocumentPhoto } from "../utils/documentOcr";
import OrderChat from "../components/OrderChat";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader, Camera, CheckCircle, X, Package, Truck, Phone,
  ChevronDown, ChevronUp, Power, Clock, DollarSign, AlertCircle,
  Star, TrendingUp, Check, Bell, Zap,
  ShieldCheck, Hash, RefreshCw, MessageCircle,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import OrderRouteMap from "../components/OrderRouteMap";

const SOCKET_URL = (process.env.REACT_APP_API_URL || "http://localhost:4000/api").replace(/\/api\/?$/, "");
const NEARBY_THRESHOLD_M = 400;
const REFRESH_INTERVAL_MS = 8000;

function haversineDistance(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Help Modal ──────────────────────────────────────────────────────────────────
const HELP_STEPS = [
  {
    emoji: "🟢",
    title: "Conéctate",
    desc: "Toca el botón 'Conectar' en la parte de arriba para aparecer como disponible y empezar a recibir pedidos.",
    tip: null,
  },
  {
    emoji: "📦",
    title: "Llega un pedido",
    desc: "Cuando haya un pedido para ti, aparecerá en la pestaña 'Disponibles'. Verás la tienda, la dirección del cliente y cuánto vas a ganar.",
    tip: "¡Tómalo rápido! Otro domiciliario puede tomarlo antes.",
    tipColor: "bg-orange-50 border-orange-200 text-orange-700",
  },
  {
    emoji: "🏍️",
    title: "Ve a recoger",
    desc: "El pedido activo te muestra la dirección de la tienda y el mapa. Ve allá lo antes posible.",
    tip: null,
  },
  {
    emoji: "🔢",
    title: "Ingresa el código",
    desc: "Al llegar a la tienda, pídele al negocio el código de 4 dígitos. Ingrésalo en la app para confirmar que ya estás ahí.",
    tip: null,
  },
  {
    emoji: "✅",
    title: "Confirma que lo recogiste",
    desc: "Con el pedido en manos, toca el botón verde grande 'Ya recogí el pedido'. Esto le avisa al cliente que vas en camino.",
    tip: null,
  },
  {
    emoji: "📸",
    title: "Entrega y toma la foto",
    desc: "Llega donde el cliente, entrega el pedido y toca 'Foto de entrega'. Toma la foto y confirma. ¡Listo, ganaste el domicilio!",
    tip: null,
  },
];

function HelpModal({ onClose }) {
  const [tab, setTab] = useState("pasos");
  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ type: "spring", damping: 28, stiffness: 320 }}
        className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[88vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-black text-gray-900">¿Cómo funciona?</h2>
            <p className="text-xs text-gray-400 mt-0.5">Guía paso a paso para domiciliarios</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition text-gray-500 text-xl font-light leading-none"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1.5 px-5 pt-3 shrink-0">
          {[
            { key: "pasos",     label: "📋 Paso a paso" },
            { key: "consejos",  label: "💡 Consejos" },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition ${tab === t.key ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-3">
          {tab === "pasos" ? (
            <>
              <p className="text-xs text-gray-400">Así funciona un pedido de inicio a fin:</p>
              {HELP_STEPS.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="flex flex-col items-center shrink-0">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 border border-amber-100 flex items-center justify-center text-lg">{step.emoji}</div>
                    {i < HELP_STEPS.length - 1 && <div className="w-0.5 bg-gray-100 mt-1" style={{ minHeight: 14 }} />}
                  </div>
                  <div className="pb-3">
                    <p className="text-xs font-black text-gray-800 mb-0.5">{i + 1}. {step.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{step.desc}</p>
                    {step.tip && (
                      <div className={`mt-1.5 text-[10px] font-semibold px-2.5 py-1.5 rounded-lg border ${step.tipColor}`}>
                        💡 {step.tip}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="space-y-3">
              {[
                { icon: "⚡", title: "Responde rápido", desc: "Los pedidos disponibles pueden ser tomados por otros domiciliarios. Cuanto más rápido aceptes, mejor." },
                { icon: "📱", title: "Los pedidos se actualizan solos", desc: "No necesitas recargar la página. Cada 8 segundos se revisan nuevos pedidos automáticamente." },
                { icon: "🔋", title: "Mantén la app abierta", desc: "Para recibir alertas de sonido cuando llega un nuevo pedido, la app debe estar en la pantalla." },
                { icon: "📍", title: "Activa el GPS", desc: "El mapa y la detección de cercanía al cliente funcionan mejor con el GPS activado." },
                { icon: "💰", title: "El domicilio aparece en ganancias", desc: "Al confirmar la entrega, el valor se suma automáticamente a tus ganancias del día." },
                { icon: "⭐", title: "Cuida tu calificación", desc: "Los clientes pueden calificarte. Una buena calificación te ayuda a conseguir más pedidos." },
              ].map((c, i) => (
                <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <span className="text-xl shrink-0">{c.icon}</span>
                  <div>
                    <p className="text-xs font-black text-gray-800 mb-0.5">{c.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 bg-amber-500 text-white text-sm font-black rounded-xl hover:bg-amber-600 transition active:scale-[0.98]"
          >
            ¡Entendido!
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ── OTP Code Input ──────────────────────────────────────────────────────────────
function OTPInput({ onComplete, onReset }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [verifying, setVerifying] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  const filled = digits.every(d => d !== "");

  const handle = (i, e) => {
    const v = e.target.value.replace(/\D/, "").slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 3) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === "Backspace") {
      const next = [...digits];
      if (next[i]) {
        next[i] = "";
        setDigits(next);
      } else if (i > 0) {
        next[i - 1] = "";
        setDigits(next);
        refs[i - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (text.length === 4) {
      setDigits(text.split(""));
      refs[3].current?.focus();
    }
    e.preventDefault();
  };

  const submit = async () => {
    if (!filled || verifying) return;
    setVerifying(true);
    try {
      await onComplete(digits.join(""));
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Código incorrecto");
      setDigits(["", "", "", ""]);
      refs[0].current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Hash size={14} className="text-amber-600" />
        <p className="text-xs font-black text-amber-700 uppercase tracking-widest">Código que te da la tienda</p>
      </div>
      <div className="flex gap-2.5 justify-center">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={refs[i]}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={e => handle(i, e)}
            onKeyDown={e => handleKeyDown(i, e)}
            onPaste={handlePaste}
            className={`w-14 h-16 text-center text-2xl font-black rounded-xl border-2 bg-white focus:outline-none transition-all ${
              d
                ? "border-amber-400 text-amber-600 bg-amber-50"
                : "border-gray-200 text-gray-800 focus:border-amber-400"
            }`}
          />
        ))}
      </div>
      <button
        onClick={submit}
        disabled={!filled || verifying}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-black text-sm transition-all disabled:opacity-40 bg-amber-500 text-white active:scale-[0.98]"
      >
        {verifying ? <Loader size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        {verifying ? "Verificando..." : "Confirmar código"}
      </button>
    </div>
  );
}

// ── Active Order Card ───────────────────────────────────────────────────────────
function ActiveOrderCard({
  order, liveLocation, deliveryPhotoState, fileInputRefs,
  onStartDelivery, onPhotoChange, onCancelPhoto, onConfirmDelivered, onVerifyCode,
}) {
  const [chatOpen, setChatOpen] = useState(() => new URLSearchParams(window.location.search).get("chat") === order.id);
  const [codeVerified, setCodeVerified] = useState(Boolean(order.pickupCodeVerifiedAt));

  const pickupLat  = order.pickupSede?.lat ?? order.pickupLat;
  const pickupLng  = order.pickupSede?.lng ?? order.pickupLng;
  const hasPickupCoords   = pickupLat != null && pickupLng != null && Number(pickupLat) !== 0 && Number(pickupLng) !== 0;
  const hasDeliveryCoords = order.deliveryLat != null && order.deliveryLng != null && Number(order.deliveryLat) !== 0 && Number(order.deliveryLng) !== 0;

  const photoState  = deliveryPhotoState[order.id];
  const isAsignado  = ["asignado", "listo"].includes(order.status);
  const isEnCamino  = order.status === "en_camino";
  const isListo     = order.status === "listo";

  const statusConfig = {
    asignado:  {
      label: "Ve a recoger el pedido",
      headerBg: "bg-amber-50 border-b border-amber-200",
      text: "text-amber-700",
      dot: "bg-amber-500",
      badge: "bg-amber-100 text-amber-700",
    },
    listo: {
      label: "¡El pedido ya está listo! Ve a buscarlo",
      headerBg: "bg-emerald-50 border-b border-emerald-200",
      text: "text-emerald-700",
      dot: "bg-emerald-500",
      badge: "bg-emerald-100 text-emerald-700",
    },
    en_camino: {
      label: "Vas en camino — lleva el pedido al cliente",
      headerBg: "bg-violet-50 border-b border-violet-200",
      text: "text-violet-700",
      dot: "bg-violet-500",
      badge: "bg-violet-100 text-violet-700",
    },
  };
  const sc = statusConfig[order.status] ?? statusConfig.asignado;

  return (
    <div className={`bg-white border rounded-2xl overflow-hidden shadow-md ${isListo ? "border-emerald-300 shadow-emerald-100" : "border-gray-200"}`}>
      {/* Header strip */}
      <div className={`flex items-center justify-between px-4 py-3 ${sc.headerBg} ${isListo ? "animate-pulse" : ""}`}>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${sc.dot} animate-pulse`} />
          <span className={`text-xs font-black ${sc.text}`}>{sc.label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono font-black text-gray-400">#{order.orderNumber}</span>
          <span className={`text-sm font-black ${sc.text}`}>
            ${Number(order.deliveryFee ?? 0).toLocaleString("es-CO")}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Tienda + Cliente */}
        <div className="flex flex-col gap-1.5">
          {order.company?.name && (
            <div className="flex items-center gap-2">
              <Package size={14} className="text-gray-400 shrink-0" />
              <span className="text-sm font-semibold text-gray-700 truncate">{order.company.name}</span>
            </div>
          )}
          {(order.user?.name || order.deliveryPhone || order.user?.phone) && (
            <div className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-100 rounded-xl px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[11px] font-black text-indigo-600 shrink-0">
                  {(order.user?.name || "C").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-black text-gray-800 truncate">{order.user?.name ?? "Cliente"}</p>
                  <p className="text-[10px] text-gray-400">Cliente del pedido</p>
                </div>
              </div>
              {(order.deliveryPhone || order.user?.phone) && (
                <a
                  href={`tel:${order.deliveryPhone ?? order.user?.phone}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-indigo-600 text-xs font-black shrink-0 hover:bg-indigo-100 transition"
                  onClick={e => e.stopPropagation()}
                >
                  <Phone size={12} />
                  {order.deliveryPhone ?? order.user?.phone}
                </a>
              )}
            </div>
          )}
        </div>

        {/* ── ASIGNADO / LISTO: recogida ── */}
        {isAsignado && (
          <div className="space-y-3">
            {/* Pickup info */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1.5">📍 Dirección de recogida</p>
              {order.pickupSede ? (
                <>
                  <p className="font-bold text-gray-800 text-sm">{order.pickupSede.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{order.pickupSede.address}</p>
                  {order.pickupSede.phone && (
                    <a href={`tel:${order.pickupSede.phone}`} className="inline-flex items-center gap-1.5 mt-2 text-amber-600 text-xs font-semibold">
                      <Phone size={12} /> {order.pickupSede.phone}
                    </a>
                  )}
                </>
              ) : (
                <p className="text-xs text-red-500">Sin punto de recogida</p>
              )}
            </div>

            <OrderRouteMap
              ryderLocation={liveLocation}
              pickupLocation={hasPickupCoords ? { lat: Number(pickupLat), lng: Number(pickupLng) } : null}
              deliveryLocation={hasDeliveryCoords ? { lat: Number(order.deliveryLat), lng: Number(order.deliveryLng) } : null}
              mode="pickup"
            />

            {/* Código de recogida */}
            {order.pickupCode && !codeVerified && (
              <OTPInput
                onComplete={async (code) => {
                  await onVerifyCode(order.id, code);
                  setCodeVerified(true);
                }}
              />
            )}
            {order.pickupCode && codeVerified && (
              <div className="flex items-center justify-center gap-2 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle size={15} className="text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700">Código verificado ✓</span>
              </div>
            )}

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => onStartDelivery(order.id)}
              disabled={Boolean(order.pickupCode) && !codeVerified}
              className={`w-full flex items-center justify-center gap-2.5 py-4 rounded-xl font-black text-base transition-all ${
                Boolean(order.pickupCode) && !codeVerified
                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                  : "bg-amber-500 text-white shadow-lg shadow-amber-200 active:scale-[0.98]"
              }`}
            >
              <CheckCircle size={20} />
              Ya recogí el pedido
            </motion.button>
          </div>
        )}

        {/* ── EN CAMINO: entrega ── */}
        {isEnCamino && (
          <div className="space-y-3">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-[10px] font-black text-violet-600 uppercase tracking-widest mb-1.5">📍 Dirección del cliente</p>
              <p className="font-bold text-gray-800 text-sm">{order.deliveryAddress}</p>
              {order.deliveryCity && <p className="text-xs text-gray-500 mt-0.5">{order.deliveryCity}</p>}
              {order.deliveryPhone && (
                <a href={`tel:${order.deliveryPhone}`} className="inline-flex items-center gap-1.5 mt-2 text-violet-600 text-xs font-semibold">
                  <Phone size={12} /> {order.deliveryPhone}
                </a>
              )}
            </div>

            <OrderRouteMap
              ryderLocation={liveLocation}
              pickupLocation={hasPickupCoords ? { lat: Number(pickupLat), lng: Number(pickupLng) } : null}
              deliveryLocation={hasDeliveryCoords ? { lat: Number(order.deliveryLat), lng: Number(order.deliveryLng) } : null}
              mode="delivery"
            />

            {!photoState ? (
              <label className="w-full flex items-center justify-center gap-2.5 bg-emerald-500 text-white py-4 rounded-xl font-black text-base cursor-pointer shadow-lg shadow-emerald-200 active:scale-[0.98] transition-transform">
                <Camera size={20} />
                Tomar foto de entrega
                <input
                  type="file" accept="image/*" capture="environment" hidden
                  ref={(el) => { fileInputRefs.current[order.id] = el; }}
                  onChange={(e) => onPhotoChange(order.id, e.target.files[0])}
                />
              </label>
            ) : (
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <img src={photoState.preview} alt="Foto de entrega" className="w-full h-44 object-cover" />
                <div className="flex gap-2 p-2 bg-gray-50">
                  <button
                    onClick={() => onCancelPhoto(order.id)}
                    className="flex-1 flex items-center justify-center gap-1.5 border border-gray-200 text-gray-500 py-2.5 rounded-xl text-sm font-semibold"
                  >
                    <X size={14} /> Cambiar foto
                  </button>
                  <button
                    onClick={() => onConfirmDelivered(order.id)}
                    disabled={photoState.uploading}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500 text-white py-2.5 rounded-xl text-sm font-black disabled:opacity-60"
                  >
                    {photoState.uploading ? <Loader size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                    Confirmar entrega
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Productos */}
        <div className="border-t border-gray-100 pt-3">
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Contenido del pedido</p>
          <div className="space-y-1.5">
            {order.items?.map((item, idx) => (
              <div key={item.id ?? item.productId ?? idx} className="flex justify-between text-sm">
                <span className="text-gray-600">{item.title} × {item.quantity}</span>
                <span className="font-semibold text-gray-800">${(Number(item.price) * item.quantity).toLocaleString("es-CO")}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between font-black text-sm border-t border-gray-100 mt-2 pt-2">
            <span className="text-gray-500">Total pedido</span>
            <span className="text-amber-600">${Number(order.total ?? 0).toLocaleString("es-CO")}</span>
          </div>
        </div>

        {/* Chat */}
        <button
          onClick={() => setChatOpen(v => !v)}
          className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl bg-gray-50 border border-gray-100 text-sm font-semibold text-gray-500 hover:bg-gray-100 transition"
        >
          <span className="flex items-center gap-2"><MessageCircle size={14} /> Chat con el cliente</span>
          {chatOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
        {chatOpen && (
          <OrderChat
            orderId={order.id}
            kind="ryder"
            user={{ id: undefined, role: "ryder" }}
            isActive={!["entregado", "cancelado"].includes(order.status)}
          />
        )}
      </div>
    </div>
  );
}

// ── Confirm Modal ───────────────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-[9999] flex items-end justify-center p-4"
      onClick={onCancel}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white border border-gray-100 rounded-2xl w-full max-w-sm p-5 space-y-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <p className="text-gray-800 text-base font-semibold text-center leading-snug">{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-500 font-semibold text-sm hover:bg-gray-50 transition">
            Cancelar
          </button>
          <button onClick={onConfirm} className="flex-1 py-3 rounded-xl bg-amber-500 text-white font-black text-sm hover:bg-amber-600 transition">
            Confirmar
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Stat chip ───────────────────────────────────────────────────────────────────
function Chip({ label, value, accent = "text-gray-900" }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3 text-center shadow-sm">
      <p className={`text-lg font-black leading-none ${accent}`}>{value}</p>
      <p className="text-[9px] font-semibold text-gray-400 mt-1 uppercase tracking-wider">{label}</p>
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────────────
export default function RyderDashboard() {
  const location = useLocation();
  const queryTab = new URLSearchParams(location.search).get("tab");

  const [activeTab, setActiveTab]           = useState(queryTab || "disponibles");
  const [ryder, setRyder]                   = useState(null);
  const [avatarSheet, setAvatarSheet]       = useState(false);
  const [docSheet, setDocSheet]             = useState(false);
  const [docFile, setDocFile]               = useState(null);
  const [docSaving, setDocSaving]           = useState(false);
  const [docError, setDocError]             = useState(null);

  const docStatus = !ryder?.user?.documentPhoto ? "missing" : ryder.user.verificationStatus;

  const uploadDocument = async () => {
    setDocError(null);
    setDocSaving(true);
    try {
      const ocr = await checkDocumentPhoto(docFile, ryder.user.documentNumber).catch(() => ({ ok: true }));
      if (!ocr.ok) { setDocError(ocr.message); return; }
      const form = new FormData();
      form.append("document", docFile);
      const res = await axios.post("/users/me/document", form);
      setRyder((prev) => ({
        ...prev,
        user: { ...prev.user, documentPhoto: res.data?.data?.documentPhoto, verificationStatus: "pendiente" },
      }));
      setDocSheet(false);
      setDocFile(null);
      toast.success("Documento enviado para revisión");
    } catch (err) {
      setDocError(err.response?.data?.message || "No se pudo enviar el documento");
    } finally { setDocSaving(false); }
  };

  const uploadRyderAvatar = async (file) => {
    const form = new FormData();
    form.append("avatar", file);
    try {
      const res = await axios.put("/ryders/me/avatar", form);
      setRyder((prev) => ({ ...prev, avatar: res.data?.data?.ryder?.avatar }));
      setAvatarSheet(false);
    } catch (err) {
      alert(err.response?.data?.message || "No se pudo guardar la foto");
    }
  };
  const [assignedOrders, setAssignedOrders] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [history, setHistory]               = useState([]);
  const [stats, setStats]                   = useState(null);
  const [invitations, setInvitations]       = useState([]);
  const [respondingInvite, setRespondingInvite] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [liveLocation, setLiveLocation]     = useState(null);
  const [deliveryPhotoState, setDeliveryPhotoState] = useState({});
  const [confirmModal, setConfirmModal]     = useState(null);
  const [showHelp,     setShowHelp]         = useState(false);
  const [refreshing,   setRefreshing]       = useState(false);
  const fileInputRefs = useRef({});
  const socketRef     = useRef(null);
  const nearbyNotifiedRef = useRef(new Set());
  const ryderRef      = useRef(null);

  const activeOrder  = assignedOrders.find(o => ["asignado", "listo", "en_camino"].includes(o.status));
  const hasActiveOrder = Boolean(activeOrder);

  // ── Socket ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    const socket = io(SOCKET_URL, { transports: ["websocket", "polling"], auth: { token } });
    socketRef.current = socket;
    socket.on("order:assigned", () => fetchAssignedOrders());
    socket.on("order:available", () => {
      if (ryderRef.current?.status === "disponible") fetchAvailableOrders();
    });
    socket.on("order:ready-for-pickup", (data) => {
      fetchAssignedOrders();
      try {
        navigator.vibrate?.([200, 100, 200, 100, 200]);
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        ctx.resume().then(() => {
          [[880, 0, 0.15], [1046, 0.18, 0.15], [880, 0.36, 0.25]].forEach(([f, s, d]) => {
            const o = ctx.createOscillator(), g = ctx.createGain();
            o.connect(g); g.connect(ctx.destination);
            o.frequency.value = f;
            g.gain.setValueAtTime(0.4, ctx.currentTime + s);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + s + d);
            o.start(ctx.currentTime + s); o.stop(ctx.currentTime + s + d + 0.05);
          });
        });
      } catch (_) {}
      toast.success(
        `✅ ¡Pedido #${data.orderNumber} listo en ${data.companyName ?? "la tienda"}! Ve a buscarlo.`,
        { autoClose: 12000, position: "top-center" }
      );
    });
    return () => { socket.disconnect(); };
  }, []); // eslint-disable-line

  // Keep ryderRef in sync so socket callbacks can read it
  useEffect(() => { ryderRef.current = ryder; }, [ryder]);

  // ── Auto-refresh available orders polling ────────────────────────────────────
  useEffect(() => {
    if (activeTab !== "disponibles") return;
    const iv = setInterval(() => {
      fetchAvailableOrders(true);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [activeTab]); // eslint-disable-line

  // ── Live GPS ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setLiveLocation(coords);
        axios.patch("/ryders/me/location", coords).catch(() => {});
        socketRef.current?.emit("ryder:location", coords);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  // ── Nearby alert ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!liveLocation || !activeOrder || activeOrder.status !== "en_camino") return;
    const { deliveryLat: dLat, deliveryLng: dLng } = activeOrder;
    if (!dLat || !dLng) return;
    const dist = haversineDistance(liveLocation.lat, liveLocation.lng, Number(dLat), Number(dLng));
    if (dist <= NEARBY_THRESHOLD_M && !nearbyNotifiedRef.current.has(activeOrder.id)) {
      nearbyNotifiedRef.current.add(activeOrder.id);
      socketRef.current?.emit("ryder:nearby", { orderId: activeOrder.id });
    }
  }, [liveLocation, activeOrder]);

  // ── Data fetching ─────────────────────────────────────────────────────────────
  useEffect(() => { fetchRyderData(); }, []); // eslint-disable-line
  useEffect(() => { if (queryTab) setActiveTab(queryTab); }, [queryTab]);
  useEffect(() => {
    if (!ryder?.id) return;
    fetchAssignedOrders();
    fetchAvailableOrders();
    fetchInvitations();
  }, [ryder?.id]); // eslint-disable-line
  useEffect(() => {
    if (!ryder) return;
    if (activeTab === "disponibles")  fetchAvailableOrders();
    if (activeTab === "historial")    fetchHistory();
    if (activeTab === "estadisticas") fetchStats();
    if (activeTab === "invitaciones") fetchInvitations();
  }, [activeTab]); // eslint-disable-line

  const fetchRyderData = async () => {
    try {
      let id = localStorage.getItem("ryderId");
      if (!id) {
        const user = JSON.parse(localStorage.getItem("user") || "null");
        if (user?.id) {
          const lookup = await axios.get(`/ryders/user/${user.id}`);
          const found = lookup.data?.data?.ryder ?? lookup.data?.data ?? lookup.data;
          if (found?.id) { id = found.id; localStorage.setItem("ryderId", id); }
        }
      }
      if (!id) { toast.error("No se encontró tu perfil"); setLoading(false); return; }
      const res = await axios.get(`/ryders/${id}`);
      const data = res.data?.data?.ryder ?? res.data?.data ?? {};
      setRyder(data);
      localStorage.setItem("ryderStatus", data.status ?? "offline");
    } catch { toast.error("Error al cargar tus datos"); }
    finally { setLoading(false); }
  };

  const fetchAssignedOrders = useCallback(async () => {
    try {
      const res = await axios.get("/orders/ryder/assigned");
      setAssignedOrders(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {}
  }, []);

  const fetchAvailableOrders = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await axios.get("/orders/available");
      setAvailableOrders(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {}
    finally { if (!silent) setRefreshing(false); }
  };

  const fetchHistory = async () => {
    try {
      const id = ryder?.id || localStorage.getItem("ryderId");
      if (!id) return;
      const res = await axios.get(`/ryders/${id}/history`);
      setHistory(res.data?.data?.data ?? res.data?.data ?? []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const id = ryder?.id || localStorage.getItem("ryderId");
      if (!id) return;
      const res = await axios.get(`/ryders/${id}/stats`);
      setStats(res.data?.data ?? null);
    } catch {}
  };

  const fetchInvitations = async () => {
    try {
      const res = await axios.get("/ryder-applications/my");
      const all = res.data?.data?.applications ?? [];
      setInvitations(all.filter(a => a.type === "invitation"));
    } catch {}
  };

  const respondToInvitation = async (appId, status) => {
    setRespondingInvite(appId);
    try {
      await axios.patch(`/ryder-applications/${appId}/respond`, { status });
      toast.success(status === "aprobado" ? "¡Te uniste a la empresa!" : "Invitación rechazada");
      fetchInvitations();
      if (status === "aprobado") fetchRyderData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al responder");
    } finally { setRespondingInvite(null); }
  };

  const handleUpdateStatus = async (orderId, status) => {
    if (!orderId || orderId === "undefined") return;
    try {
      await axios.patch(`/orders/${orderId}/status`, { status });
      toast.success("Estado actualizado");
      fetchAssignedOrders();
      if (status === "entregado") { fetchHistory(); fetchRyderData(); }
    } catch { toast.error("Error al actualizar estado"); }
  };

  const handleAcceptOrder = async (orderId) => {
    try {
      await axios.patch(`/orders/${orderId}/self-assign`);
      toast.success("¡Pedido aceptado!");
      fetchAssignedOrders();
      fetchAvailableOrders();
      await fetchRyderData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al aceptar pedido");
    }
  };

  const toggleStatus = async () => {
    if (hasActiveOrder) return;
    const newStatus = ryder.status === "disponible" ? "offline" : "disponible";
    try {
      await axios.patch("/ryders/me/status", { status: newStatus });
      setRyder({ ...ryder, status: newStatus });
      localStorage.setItem("ryderStatus", newStatus);
      window.dispatchEvent(new CustomEvent("ryderStatusChange", { detail: newStatus }));
      toast.success(newStatus === "disponible" ? "¡Estás conectado! Recibirás pedidos." : "Te desconectaste.");
    } catch { toast.error("Error al actualizar estado"); }
  };

  const onStartDelivery = (orderId) => {
    setConfirmModal({
      message: "¿Ya tienes el pedido en tus manos y estás listo para salir?",
      onConfirm: () => { setConfirmModal(null); handleUpdateStatus(orderId, "en_camino"); },
    });
  };

  const handleDeliveryPhotoChange = (orderId, file) => {
    if (!file) return;
    setDeliveryPhotoState(prev => ({ ...prev, [orderId]: { file, preview: URL.createObjectURL(file), uploading: false } }));
  };

  const cancelDeliveryPhoto = (orderId) => {
    setDeliveryPhotoState(prev => {
      const next = { ...prev };
      if (next[orderId]?.preview) URL.revokeObjectURL(next[orderId].preview);
      delete next[orderId];
      return next;
    });
    if (fileInputRefs.current[orderId]) fileInputRefs.current[orderId].value = "";
  };

  const verifyPickupCode = async (orderId, code) => {
    await axios.post(`/orders/${orderId}/verify-pickup-code`, { code });
  };

  const confirmDelivered = async (orderId) => {
    const photoData = deliveryPhotoState[orderId];
    if (!photoData?.file) return;
    setDeliveryPhotoState(prev => ({ ...prev, [orderId]: { ...prev[orderId], uploading: true } }));
    try {
      const fd = new FormData();
      fd.append("photo", photoData.file);
      await axios.patch(`/orders/${orderId}/deliver`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("¡Entrega confirmada! 🎉");
      cancelDeliveryPhoto(orderId);
      fetchAssignedOrders();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error al confirmar entrega");
      setDeliveryPhotoState(prev => ({ ...prev, [orderId]: { ...prev[orderId], uploading: false } }));
    }
  };

  // ── Render guards ────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center">
          <Truck size={28} className="text-amber-500 animate-pulse" />
        </div>
        <p className="text-gray-400 text-sm font-semibold">Cargando tu panel...</p>
      </div>
    </div>
  );

  if (!ryder) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center">
      <div className="space-y-3">
        <AlertCircle size={40} className="text-red-400 mx-auto" />
        <p className="text-gray-600 font-semibold text-sm">No se pudo cargar el perfil de domiciliario.</p>
      </div>
    </div>
  );

  if (ryder?.approvalStatus === "pendiente") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center">
      <div className="space-y-4 max-w-xs">
        <div className="w-20 h-20 rounded-3xl bg-amber-100 flex items-center justify-center mx-auto">
          <Clock size={32} className="text-amber-500" />
        </div>
        <p className="text-gray-800 font-black text-lg">Tu solicitud está en revisión</p>
        <p className="text-gray-500 text-sm">Te notificaremos cuando sea aprobada. ¡Gracias por tu paciencia!</p>
      </div>
    </div>
  );

  if (ryder?.approvalStatus === "inactivo") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 text-center">
      <div className="space-y-3">
        <AlertCircle size={40} className="text-red-400 mx-auto" />
        <p className="text-red-500 font-bold">Tu cuenta ha sido inactivada.</p>
        <p className="text-gray-400 text-sm">Contacta con soporte para más información.</p>
      </div>
    </div>
  );

  const statusMeta = {
    disponible: { label: "Conectado — recibiendo pedidos", dot: "bg-emerald-500", ring: "ring-emerald-300", text: "text-emerald-600" },
    ocupado:    { label: "En un pedido",                   dot: "bg-amber-500",   ring: "ring-amber-300",   text: "text-amber-600"  },
    offline:    { label: "Desconectado",                   dot: "bg-gray-300",    ring: "ring-gray-200",    text: "text-gray-400"   },
  };
  const sm = statusMeta[ryder.status] ?? statusMeta.offline;

  const pendingInvites = invitations.filter(i => i.status === "pendiente").length;

  const TABS = [
    { key: "disponibles",  label: "Disponibles", icon: Zap,       count: availableOrders.length },
    { key: "historial",    label: "Entregas",     icon: Package,   count: null },
    { key: "estadisticas", label: "Ganancias",    icon: DollarSign, count: null },
    { key: "invitaciones", label: "Empresas",     icon: Bell,       count: pendingInvites || null },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <AnimatePresence>
        {confirmModal && (
          <ConfirmModal
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(null)}
          />
        )}
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      </AnimatePresence>

      {ryder && docStatus !== "aprobado" && (
        <div className={`border-b px-4 py-3 ${docStatus === "pendiente" ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}>
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <p className={`text-sm ${docStatus === "pendiente" ? "text-blue-800" : "text-red-800"}`}>
              {docStatus === "pendiente" && "Tu documento de identidad está en revisión."}
              {docStatus === "missing" && "Falta tu documento de identidad. Tómale foto o súbelo para validar tu cuenta."}
              {docStatus === "rechazado" && "Tu documento fue rechazado. Vuelve a tomarle la foto o súbelo."}
            </p>
            {docStatus !== "pendiente" && (
              <button type="button" onClick={() => { setDocError(null); setDocSheet(true); }}
                className="px-3 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-bold whitespace-nowrap">
                {docStatus === "missing" ? "Subir documento" : "Subir nuevo"}
              </button>
            )}
          </div>
        </div>
      )}

      {docSheet && ryder && (
        <div className="fixed inset-0 bg-black/60 z-[10020] flex items-end sm:items-center justify-center p-4" onClick={() => setDocSheet(false)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-gray-900">Documento de identidad</p>
              <button type="button" onClick={() => setDocSheet(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Número registrado: {ryder.user?.documentNumber || "—"}</p>
            <DocumentCapture onAccept={(file) => { setDocFile(file); setDocError(null); }} />
            {docError && <p className="text-xs text-red-600 mt-3">{docError}</p>}
            <button type="button" onClick={uploadDocument} disabled={!docFile || docSaving}
              className="mt-4 w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold flex items-center justify-center gap-2">
              {docSaving && <RefreshCw size={14} className="animate-spin" />}
              {docSaving ? "Verificando documento…" : "Enviar documento"}
            </button>
          </div>
        </div>
      )}

      {ryder && !ryder.avatar && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800">Sube tu foto de perfil. Los clientes la verán cuando les lleves su pedido.</p>
            <button type="button" onClick={() => setAvatarSheet(true)}
              className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold whitespace-nowrap">
              Subir foto
            </button>
          </div>
        </div>
      )}

      {avatarSheet && ryder && (
        <div className="fixed inset-0 bg-black/60 z-[10020] flex items-end sm:items-center justify-center p-4" onClick={() => setAvatarSheet(false)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-gray-900">Foto de perfil</p>
              <button type="button" onClick={() => setAvatarSheet(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <AvatarCapture currentUrl={getImageUrl(ryder.avatar)} onAccept={uploadRyderAvatar} />
          </div>
        </div>
      )}

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-gray-100 px-4 pt-4 pb-4 shadow-sm">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between gap-3">
            {/* Identity */}
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setAvatarSheet(true)}
                title="Cambiar foto de perfil"
                className={`relative w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center font-black text-white text-xl ring-4 ${sm.ring} shadow-md overflow-hidden`}>
                {ryder.avatar
                  ? <img src={getImageUrl(ryder.avatar)} alt="Foto de perfil" className="w-full h-full object-cover" />
                  : ryder.user?.name?.charAt(0).toUpperCase()}
                <span className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${sm.dot}`} />
              </button>
              <div>
                <p className="font-black text-gray-900 text-base leading-tight">{ryder.user?.name?.split(" ")[0]}</p>
                <p className={`text-xs font-semibold flex items-center gap-1 ${sm.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${sm.dot} ${ryder.status === "disponible" ? "animate-pulse" : ""}`} />
                  {sm.label}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Help button */}
              <button
                onClick={() => setShowHelp(true)}
                className="w-9 h-9 flex items-center justify-center text-base font-black bg-amber-50 text-amber-600 border border-amber-200 rounded-xl hover:bg-amber-100 transition"
              >
                ?
              </button>

              {/* Status toggle */}
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={toggleStatus}
                disabled={hasActiveOrder}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs transition-all ${
                  hasActiveOrder
                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                    : ryder.status === "disponible"
                    ? "bg-red-50 border border-red-200 text-red-500 hover:bg-red-100"
                    : "bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100"
                }`}
              >
                <Power size={14} />
                {hasActiveOrder ? "En curso" : ryder.status === "disponible" ? "Desconectar" : "Conectar"}
              </motion.button>
            </div>
          </div>

          {/* Mini stats strip */}
          <div className="grid grid-cols-3 gap-2 mt-3">
            <Chip label="Entregas totales" value={ryder.totalDeliveries ?? 0} />
            <Chip label="Calificación" value={ryder.rating ? Number(ryder.rating).toFixed(1) : "—"} accent="text-amber-500" />
            <Chip label="Ingresos hoy" value={ryder.earningsToday != null ? `$${Number(ryder.earningsToday).toLocaleString("es-CO")}` : "—"} accent="text-emerald-600" />
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4 space-y-4">

        {/* ── Active order ─────────────────────────────────────────────────── */}
        <AnimatePresence mode="popLayout">
          {activeOrder && (
            <motion.div
              key={activeOrder.id}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
            >
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">📦 Pedido activo</p>
              <ActiveOrderCard
                order={activeOrder}
                liveLocation={liveLocation}
                deliveryPhotoState={deliveryPhotoState}
                fileInputRefs={fileInputRefs}
                onStartDelivery={onStartDelivery}
                onPhotoChange={handleDeliveryPhotoChange}
                onCancelPhoto={cancelDeliveryPhoto}
                onConfirmDelivered={confirmDelivered}
                onVerifyCode={verifyPickupCode}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Tabs ─────────────────────────────────────────────────────────── */}
        <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
          {/* Tab bar */}
          <div className="flex border-b border-gray-100">
            {TABS.map(({ key, label, icon: Icon, count }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 flex flex-col items-center gap-1 py-3 text-[10px] font-bold uppercase tracking-wider relative transition-colors ${
                  activeTab === key ? "text-amber-500" : "text-gray-300 hover:text-gray-500"
                }`}
              >
                <Icon size={15} />
                <span>{label}</span>
                {count > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 flex items-center justify-center bg-red-500 text-white text-[8px] font-black rounded-full px-1">
                    {count}
                  </span>
                )}
                {activeTab === key && (
                  <motion.div layoutId="tab-indicator" className="absolute bottom-0 left-2 right-2 h-0.5 bg-amber-500 rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* ── Disponibles ──────────────────────────────────────────────── */}
          {activeTab === "disponibles" && (
            <div>
              {/* Refresh bar */}
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-50 bg-gray-50/60">
                <p className="text-[10px] text-gray-400 font-semibold flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Se actualiza automáticamente
                </p>
                <button
                  onClick={() => fetchAvailableOrders()}
                  disabled={refreshing}
                  className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-gray-600 transition disabled:opacity-50"
                >
                  <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
                  Actualizar
                </button>
              </div>

              {availableOrders.length === 0 ? (
                <div className="py-16 text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Package size={26} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-bold text-gray-500">Todo tranquilo por ahora</p>
                  <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
                    Cuando llegue un pedido disponible, aparecerá aquí con una alerta de sonido.
                  </p>
                  {ryder.status !== "disponible" && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 font-semibold">
                      ⚡ Conéctate primero para recibir pedidos
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {availableOrders.map(order => (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, x: -4 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="p-4 space-y-3"
                    >
                      {/* Order header */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                          <span className="font-black text-gray-800 text-sm">Pedido #{order.orderNumber}</span>
                        </div>
                        {order.company?.name && (
                          <span className="text-[10px] font-semibold text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full">
                            {order.company.name}
                          </span>
                        )}
                      </div>

                      {/* Ganancia destacada */}
                      {order.deliveryFee != null && (
                        <div className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl py-2.5">
                          <DollarSign size={16} className="text-emerald-600" />
                          <span className="text-xl font-black text-emerald-700">${Number(order.deliveryFee).toLocaleString("es-CO")}</span>
                          <span className="text-xs text-emerald-500 font-semibold">de domicilio</span>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2.5">
                          <p className="text-[9px] font-black text-amber-600 uppercase tracking-widest mb-1">📍 Recogida</p>
                          <p className="text-gray-800 text-xs font-semibold leading-tight">{order.pickupSede?.name ?? "—"}</p>
                          <p className="text-gray-400 text-[10px] mt-0.5 truncate">{order.pickupSede?.address ?? ""}</p>
                        </div>
                        <div className="bg-violet-50 border border-violet-200 rounded-xl p-2.5">
                          <p className="text-[9px] font-black text-violet-600 uppercase tracking-widest mb-1">🏠 Entrega</p>
                          <p className="text-gray-800 text-xs font-semibold leading-tight">{order.deliveryAddress ?? "—"}</p>
                          <p className="text-gray-400 text-[10px] mt-0.5">{order.deliveryCity ?? ""}</p>
                        </div>
                      </div>

                      {/* Productos */}
                      {order.items?.length > 0 && (
                        <div className="space-y-1">
                          {order.items.slice(0, 2).map((item, idx) => (
                            <div key={item.id ?? item.productId ?? idx} className="flex justify-between text-xs text-gray-500">
                              <span>{item.title} × {item.quantity}</span>
                              <span>${(Number(item.price) * item.quantity).toLocaleString("es-CO")}</span>
                            </div>
                          ))}
                          {order.items.length > 2 && (
                            <p className="text-[10px] text-gray-400">+{order.items.length - 2} producto(s) más</p>
                          )}
                          <div className="flex justify-between font-black text-sm border-t border-gray-100 pt-1.5">
                            <span className="text-gray-400">Total del pedido</span>
                            <span className="text-gray-800">${Number(order.total ?? 0).toLocaleString("es-CO")}</span>
                          </div>
                        </div>
                      )}

                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleAcceptOrder(order.id)}
                        disabled={hasActiveOrder || ryder.status !== "disponible"}
                        className={`w-full py-4 rounded-xl font-black text-base transition-all ${
                          hasActiveOrder
                            ? "bg-gray-100 text-gray-300 cursor-not-allowed text-sm"
                            : ryder.status !== "disponible"
                            ? "bg-gray-100 text-gray-400 cursor-not-allowed text-sm"
                            : "bg-amber-500 text-white shadow-lg shadow-amber-200 active:scale-[0.98]"
                        }`}
                      >
                        {hasActiveOrder
                          ? "Ya tienes un pedido activo"
                          : ryder.status !== "disponible"
                          ? "Conéctate para aceptar pedidos"
                          : "⚡ Aceptar este pedido"
                        }
                      </motion.button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Historial ────────────────────────────────────────────────── */}
          {activeTab === "historial" && (
            <div>
              {history.length === 0 ? (
                <div className="py-16 text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Package size={26} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-bold text-gray-500">Sin entregas aún</p>
                  <p className="text-xs text-gray-400 mt-1">Tus entregas completadas aparecerán aquí.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {history.map(order => (
                    <div key={order.id} className="p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-black text-gray-800 text-sm">#{order.orderNumber}</span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">✓ Entregado</span>
                      </div>
                      {order.company?.name && <p className="text-xs text-gray-400">{order.company.name}</p>}
                      <p className="text-xs text-gray-500">{order.deliveryAddress}</p>
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1 text-emerald-600 font-black text-sm">
                          <DollarSign size={13} />
                          {Number(order.deliveryFee || 0).toLocaleString("es-CO")}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {order.deliveredAt ? new Date(order.deliveredAt).toLocaleDateString("es-CO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Estadísticas / Ganancias ──────────────────────────────────── */}
          {activeTab === "estadisticas" && (
            <div className="p-4">
              {!stats ? (
                <div className="py-14 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                    <TrendingUp size={22} className="text-gray-300" />
                  </div>
                  <p className="text-sm text-gray-400 font-semibold">Cargando ganancias...</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {/* Ganancias */}
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                      <TrendingUp size={12} /> Ganancias por domicilios
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: "Hoy",    value: stats.earnings.today },
                        { label: "Semana", value: stats.earnings.week },
                        { label: "Mes",    value: stats.earnings.month },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
                          <p className="text-[9px] text-gray-400 mb-0.5">{label}</p>
                          <p className="text-sm font-black text-emerald-600">
                            ${Number(value).toLocaleString("es-CO")}
                          </p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] text-gray-400 text-right mt-1.5">
                      Total acumulado: <span className="text-gray-600 font-semibold">${Number(stats.earnings.total).toLocaleString("es-CO")}</span>
                    </p>
                  </div>

                  {/* Entregas */}
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                      <Package size={12} /> Entregas realizadas
                    </p>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label: "Hoy",    value: stats.deliveries.today },
                        { label: "Semana", value: stats.deliveries.week },
                        { label: "Mes",    value: stats.deliveries.month },
                      ].map(({ label, value }) => (
                        <div key={label} className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                          <p className="text-[9px] text-gray-400 mb-0.5">{label}</p>
                          <p className="text-lg font-black text-amber-600">{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Calificación */}
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                      <Star size={12} /> Tu calificación
                    </p>
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center gap-4">
                      <div className="text-center shrink-0">
                        <p className="text-4xl font-black text-amber-500">
                          {stats.rating.count > 0 ? stats.rating.average.toFixed(1) : "—"}
                        </p>
                        <div className="flex gap-0.5 justify-center mt-1">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} size={11} className={s <= Math.round(stats.rating.average) ? "text-amber-400 fill-amber-400" : "text-gray-200 fill-gray-200"} />
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="font-black text-gray-800 text-sm">{stats.rating.count} calificaciones</p>
                        <p className="text-xs text-gray-400 mt-0.5">de clientes</p>
                      </div>
                    </div>

                    {stats.reviews?.length > 0 && (
                      <div className="space-y-2.5 mt-3">
                        {stats.reviews.map((r, i) => (
                          <div key={i} className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm">
                            <div className="flex items-center justify-between mb-1.5">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-[10px] font-black text-amber-600">
                                  {r.userName?.charAt(0)?.toUpperCase()}
                                </div>
                                <span className="text-xs font-semibold text-gray-600">{r.userName}</span>
                              </div>
                              <div className="flex gap-0.5">
                                {[1,2,3,4,5].map(s => (
                                  <Star key={s} size={10} className={s <= (r.score ?? 0) ? "text-amber-400 fill-amber-400" : "text-gray-200 fill-gray-200"} />
                                ))}
                              </div>
                            </div>
                            {r.comment && <p className="text-xs text-gray-500 leading-snug">{r.comment}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Invitaciones ─────────────────────────────────────────────── */}
          {activeTab === "invitaciones" && (
            <div>
              {invitations.length === 0 ? (
                <div className="py-16 text-center px-6">
                  <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
                    <Bell size={24} className="text-gray-300" />
                  </div>
                  <p className="text-sm font-bold text-gray-500">Sin invitaciones de empresas</p>
                  <p className="text-xs text-gray-400 mt-1">Cuando una empresa de domicilios te invite, aparecerá aquí.</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {invitations.map(inv => {
                    const isPending = inv.status === "pendiente";
                    const dc = inv.deliveryCompany;
                    return (
                      <div key={inv.id} className="p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-400 to-violet-500 flex items-center justify-center text-white text-xs font-black shrink-0">
                            {(dc?.name || "?").slice(0, 2).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-black text-gray-800 text-sm">{dc?.name ?? "Empresa"}</p>
                              {dc?.verified && <span className="text-[10px] text-blue-500 bg-blue-50 border border-blue-100 font-bold px-1.5 py-0.5 rounded-full">✓ Verificada</span>}
                            </div>
                            {dc?.city && <p className="text-xs text-gray-400 mt-0.5">{dc.city}</p>}
                            {inv.message && (
                              <p className="text-xs text-gray-500 mt-1.5 bg-gray-50 rounded-lg px-2 py-1 border border-gray-100 italic">"{inv.message}"</p>
                            )}
                          </div>
                          <span className={`text-[10px] font-black px-2 py-1 rounded-full shrink-0 ${
                            inv.status === "pendiente"  ? "bg-amber-100 text-amber-700" :
                            inv.status === "aprobado"   ? "bg-emerald-100 text-emerald-700" :
                                                          "bg-red-100 text-red-500"
                          }`}>
                            {inv.status === "pendiente" ? "Pendiente" : inv.status === "aprobado" ? "Aceptada" : "Rechazada"}
                          </span>
                        </div>

                        {isPending && (
                          <div className="flex gap-2">
                            <motion.button
                              whileTap={{ scale: 0.97 }}
                              onClick={() => respondToInvitation(inv.id, "aprobado")}
                              disabled={respondingInvite === inv.id}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-emerald-500 text-white font-black text-sm disabled:opacity-50 hover:bg-emerald-600 transition"
                            >
                              <Check size={14} />
                              {respondingInvite === inv.id ? "..." : "Aceptar"}
                            </motion.button>
                            <motion.button
                              whileTap={{ scale: 0.97 }}
                              onClick={() => respondToInvitation(inv.id, "rechazado")}
                              disabled={respondingInvite === inv.id}
                              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-red-200 text-red-500 font-bold text-sm hover:bg-red-50 transition disabled:opacity-50"
                            >
                              <X size={14} />
                              Rechazar
                            </motion.button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
